import React, {
    ClipboardEvent,
    KeyboardEvent,
    MutableRefObject, SyntheticEvent,
    useCallback,
    useEffect,
    useRef,
} from 'react';
import {useDsManager, useRootStore} from "@stores/AppStateContext";
import {observer} from "mobx-react-lite";
import classNames from "classnames";

import {ItemModelType} from "@stores/WorkspaceStore";
import {useCrudServices} from "../../../../../platform/hooks/useServiceCombinations";
import type {CollabEditor} from '../../../../../Editor/CollabEditor';

import {updateItemName} from "../../../../../actions/item/updateItem";
import {pasteAsText} from "../../../../../utils/input/pasteAsText";


import './NodeTitle.css';


interface NodeTitleProps {
    collabEditorViewRef: MutableRefObject<CollabEditor | null>,
    selectedNode: ItemModelType,
    isReadOnly: boolean
}

const NodeTitleBase: React.FunctionComponent<NodeTitleProps> = (
    {
        collabEditorViewRef,
        isReadOnly
    }: NodeTitleProps
) => {
    const rootStore = useRootStore();
    const crudServices = useCrudServices();
    const dsManager = useDsManager()!;

    const inputRef = useRef<HTMLInputElement>(null);
    const selectedNode = rootStore.view.workspace!.selected!;
    const selectedNodeName = selectedNode.name;

    const handeKeyPress = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
            if (collabEditorViewRef.current !== null) {
                collabEditorViewRef.current!.insertNewLine();
            }
            event.preventDefault();
            event.stopPropagation();
        }
        if (event.key === 'ArrowDown') {
            if (collabEditorViewRef.current !== null) {
                collabEditorViewRef.current!.focusFirstLine();
            }
            event.preventDefault();
            event.stopPropagation();
        }
    }

    useEffect(() => {
        if (selectedNodeName !== inputRef.current!.textContent) {
            inputRef.current!.textContent = selectedNodeName;
        }
    }, [selectedNodeName]);

    useEffect(() => {
        if (selectedNode.name === '' && !isReadOnly) {
            inputRef.current!.focus();
        }
    }, [selectedNode, isReadOnly]);

    const onInput = useCallback((event: SyntheticEvent<HTMLDivElement>) => {
        selectedNode.setIsRenaming(true);
        const node = event.target as any;
        if (node.textContent !== selectedNodeName) {
            updateItemName(crudServices, dsManager, selectedNode, node.textContent)
        }
    }, [selectedNode, selectedNodeName]);

    const onPaste = useCallback((event: ClipboardEvent<HTMLDivElement>) => {
        selectedNode.setIsRenaming(true);
        pasteAsText(event);
        if (inputRef.current!.textContent !== selectedNodeName) {
            updateItemName(crudServices, dsManager, selectedNode, inputRef.current!.textContent!)
        }
    }, [selectedNode, selectedNodeName])

    const onBlur = useCallback((event: SyntheticEvent<HTMLDivElement>) => {
        selectedNode.setIsRenaming(false);
    }, [selectedNode]);

    return (
        <h1
            className="NodeTitle display-small-bold"
        >
            <div
                ref={inputRef}
                contentEditable={isReadOnly ? 'false' : 'true'}
                className={
                    classNames({
                        "NodeTitle__input": true,
                        "NodeTitle__input--readOnly": isReadOnly
                    })
                }
                onKeyDown={!isReadOnly ? handeKeyPress : undefined}
                spellCheck={false}
                onInput={onInput}
                onBlur={!isReadOnly ? onBlur : undefined}
                onDragOver={(event) => event.preventDefault()}
                onPaste={onPaste}
            />
            {
                selectedNode.name.length === 0
                    ? <NodeTitlePlaceholder/>
                    : null
            }
        </h1>
    )
}

export const NodeTitle = observer(NodeTitleBase);

export const NodeTitlePlaceholder = observer(() => {
    const rootStore = useRootStore();

    const selectedNode = rootStore.view.workspace!.selected!;
    const placeholder = selectedNode.getNamePlaceholder(rootStore.currentLocale);
    return (
        <div
            key={rootStore.currentLocale}
            className='title-placeholder'
        >
            {placeholder}
        </div>
    )
});
